import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { postFetcher } from 'features/rolodex/fetchers'
import useSWR, { SWRConfiguration } from 'swr'
import { RolodexFilterType } from 'types'

type RolodexLoadOptions = {
  type?: string | string[]
  tags?: Record<string, string | string[]>
  filters?: RolodexFilterType[]
  advanced_filter?: {
    operator: string
    filters: RolodexFilterType[]
  }
  order_by?: Record<string, unknown>[]
  ids?: string[]
  limit?: number
  links?: string[]
}

export function useGetRolodexEntriesByLoadOptions<T>(
  loadOptions: RolodexLoadOptions | undefined,
  swrConfig?: SWRConfiguration<T, Error>
) {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()
  const endpoint = `${ROLODEX_URL}/api/v2/entries/load`
  const bodyData = JSON.stringify(loadOptions)
  const shouldFetch =
    isAuthenticated && loadOptions !== undefined && loadOptions.limit !== 0
  const { data, error, isLoading, isValidating, mutate } = useSWR<
    T,
    Error,
    [string, string] | null
  >(
    shouldFetch ? [endpoint, bodyData] : null,
    ([endpoint, bodyData]) => postFetcher(endpoint, bodyData, axios),
    swrConfig
  )

  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  }
}

export function useGetRolodexAuditLog<T>(
  entryIDs: string[],
  options?: { startDate?: string; endDate?: string }
) {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()

  const body = JSON.stringify({
    entry_ids: entryIDs,
    ...(options?.startDate && { start_date: options.startDate }),
    ...(options?.endDate && { end_date: options.endDate }),
  })

  const { ROLODEX_URL } = useRuntimeVariables()
  const endpoint = `${ROLODEX_URL}/api/v2/audit-log/entries`
  const { data, error, isLoading, isValidating, mutate } = useSWR<
    T,
    Error,
    [string, string] | null
  >(isAuthenticated ? [endpoint, body] : null, ([endpoint, body]) =>
    postFetcher(endpoint, body, axios)
  )

  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  }
}
